import React from 'react'
import clsx from 'clsx'
import {
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  useTheme
} from '@mui/material'
import { CloseCircle } from 'iconsax-react'

import Accordion from '../../../../../../components/molecules/Accordion'
import LocalFilterInfo from '../../../../../../components/atoms/LocalFilterInfo'

import { useTaskFilters } from '../../../../../../providers/TaskFiltersProvider'
import useStyles from './styles'

const Wrapper = ({ menuMode, children, ...props }) => {
  if (menuMode) {
    return <>{children}</>
  }

  // @ts-expect-error Props will be fine
  return <Accordion {...props}>{children}</Accordion>
}

type MenuSectionProps = {
  title: string
  options: string[]
  value: string
  setValue: (val: string) => void
  transform?: (val: string) => string
  hideNullOption?: boolean
  testId?: string
  showInfo?: boolean
  checkFilters?: boolean
  unassignedOpt?: string
  nullOpt?: string
  noBorder?: boolean
  menuMode?: boolean
  closeModal?: () => void
  children?: React.ReactNode
}

const getValue = (val, transform, unassignedOpt) =>
  val === 'null' ? unassignedOpt : transform(val)

const MenuSection: React.FC<MenuSectionProps> = ({
  title,
  options,
  value,
  setValue,
  transform = val => val,
  hideNullOption,
  testId,
  showInfo,
  checkFilters,
  unassignedOpt = 'Unassigned',
  nullOpt = 'null',
  noBorder,
  menuMode,
  closeModal,
  children
}) => {
  const theme = useTheme()

  const { expanded, toggleExpand } = useTaskFilters()

  const isExpanded = React.useMemo(
    () => expanded.includes(title),
    [expanded, title]
  )

  // Expand this section when a filter is selected, after a load
  React.useEffect(() => {
    if (isExpanded || !checkFilters) {
      return
    }

    if (value !== nullOpt) {
      toggleExpand(title)
    }
  }, [isExpanded, checkFilters, value, nullOpt, toggleExpand, title])

  const opts = React.useMemo(() => {
    if (hideNullOption) {
      return options
    }

    return ['null', ...options]
  }, [hideNullOption, options])

  const classes = useStyles({ noBorder: noBorder || menuMode })

  return (
    <div className={classes.root} data-testid={testId}>
      {menuMode && closeModal && (
        <div className={classes.menuTitleArea}>
          <div className={classes.menuTitle}>{title}</div>

          <div
            onClick={e => {
              e.stopPropagation()
              closeModal()
            }}
            className={classes.menuClose}
          >
            <CloseCircle
              variant='Bold'
              size={theme.size.base}
              color={theme.palette.primary.main}
            />
          </div>
        </div>
      )}

      <Wrapper
        menuMode={menuMode}
        title={showInfo ? <LocalFilterInfo title={title} /> : title}
        expanded={isExpanded}
        toggleOpen={() => toggleExpand(title)}
      >
        <div>
          <FormControl
            className={clsx(
              classes.control,
              menuMode && classes.menuFormControl
            )}
          >
            <RadioGroup
              name={title}
              value={value}
              onChange={e => setValue(e.target.value)}
            >
              {opts.map((val, i) => (
                <FormControlLabel
                  key={i}
                  value={val}
                  label={getValue(val, transform, unassignedOpt)}
                  control={
                    <Radio
                      color='primary'
                      size='small'
                      className={classes.radio}
                    />
                  }
                  className={clsx(classes.controlLabel, {
                    [classes.controlLabelBold]: val === value
                  })}
                />
              ))}
            </RadioGroup>
          </FormControl>

          {children}
        </div>
      </Wrapper>
    </div>
  )
}

export default MenuSection
